import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Mail, 
  Phone, 
  User, 
  Calendar, 
  Wrench, 
  Trash2, 
  CheckCircle, 
  Clock, 
  XCircle,
  ArrowLeft,
  RefreshCw,
  MessageSquare,
  FileText
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin Panel Component
export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contactsRes, quotesRes, statsRes] = await Promise.all([
        axios.get(`${API}/contact`),
        axios.get(`${API}/quote`),
        axios.get(`${API}/admin/stats`)
      ]);
      setContacts(contactsRes.data.reverse());
      setQuotes(quotesRes.data.reverse());
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const updateContactStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/contact/${id}?status=${status}`);
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const updateQuoteStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quote/${id}?status=${status}`);
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    try {
      await axios.delete(`${API}/admin/contact/${id}`);
      toast.success("Message deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Are you sure you want to delete this quote?")) return;
    try {
      await axios.delete(`${API}/admin/quote/${id}`);
      toast.success("Quote deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
      case "pending":
        return "bg-yellow-500/20 text-yellow-500";
      case "replied":
      case "completed":
        return "bg-green-500/20 text-green-500";
      case "closed":
        return "bg-gray-500/20 text-gray-500";
      default:
        return "bg-gray-500/20 text-gray-500";
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#121212',
            color: '#F3F4F6',
            border: '1px solid #27272A'
          }
        }}
      />
      
      {/* Header */}
      <header className="bg-[#121212] border-b border-[#27272A] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={20} />
              <span>Back to Site</span>
            </a>
            <div className="h-6 w-px bg-[#27272A]"></div>
            <h1 className="text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              <span className="text-[#7C3AED]">R&O</span> Admin Panel
            </h1>
          </div>
          <button 
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-[#7C3AED] rounded-lg hover:bg-[#6D28D9] transition-colors"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#121212] border border-[#27272A] rounded-lg p-4">
              <p className="text-gray-400 text-sm">Total Messages</p>
              <p className="text-3xl font-bold text-white">{stats.total_contacts}</p>
            </div>
            <div className="bg-[#121212] border border-[#27272A] rounded-lg p-4">
              <p className="text-gray-400 text-sm">New Messages</p>
              <p className="text-3xl font-bold text-yellow-500">{stats.new_contacts}</p>
            </div>
            <div className="bg-[#121212] border border-[#27272A] rounded-lg p-4">
              <p className="text-gray-400 text-sm">Total Quotes</p>
              <p className="text-3xl font-bold text-white">{stats.total_quotes}</p>
            </div>
            <div className="bg-[#121212] border border-[#27272A] rounded-lg p-4">
              <p className="text-gray-400 text-sm">Pending Quotes</p>
              <p className="text-3xl font-bold text-[#7C3AED]">{stats.pending_quotes}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab("contacts")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
              activeTab === "contacts"
                ? "bg-[#7C3AED] text-white"
                : "bg-[#121212] text-gray-400 hover:text-white"
            }`}
          >
            <MessageSquare size={18} />
            Messages ({contacts.length})
          </button>
          <button
            onClick={() => setActiveTab("quotes")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
              activeTab === "quotes"
                ? "bg-[#7C3AED] text-white"
                : "bg-[#121212] text-gray-400 hover:text-white"
            }`}
          >
            <FileText size={18} />
            Quotes ({quotes.length})
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="animate-spin mx-auto mb-4" size={32} />
            <p className="text-gray-400">Loading...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activeTab === "contacts" && contacts.length === 0 && (
              <div className="bg-[#121212] border border-[#27272A] rounded-lg p-8 text-center">
                <MessageSquare className="mx-auto mb-4 text-gray-500" size={48} />
                <p className="text-gray-400">No messages yet</p>
              </div>
            )}

            {activeTab === "contacts" && contacts.map((contact) => (
              <div key={contact.id} className="bg-[#121212] border border-[#27272A] rounded-lg p-5">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#7C3AED]/20 rounded-full flex items-center justify-center">
                      <User className="text-[#7C3AED]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{contact.name}</h3>
                      <p className="text-sm text-gray-400">{contact.service || "General Inquiry"}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(contact.status)}`}>
                    {contact.status}
                  </span>
                </div>

                <p className="text-gray-300 mb-4">{contact.message}</p>

                <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-4">
                  <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-[#7C3AED]">
                    <Mail size={14} />
                    {contact.email}
                  </a>
                  <a href={`tel:${contact.phone}`} className="flex items-center gap-1 hover:text-[#7C3AED]">
                    <Phone size={14} />
                    {contact.phone}
                  </a>
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {formatDate(contact.created_at)}
                  </span>
                </div>

                <div className="flex gap-2 pt-3 border-t border-[#27272A]">
                  <button
                    onClick={() => updateContactStatus(contact.id, "replied")}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-500/20 text-green-500 rounded text-sm hover:bg-green-500/30"
                  >
                    <CheckCircle size={14} />
                    Replied
                  </button>
                  <button
                    onClick={() => updateContactStatus(contact.id, "closed")}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-500/20 text-gray-400 rounded text-sm hover:bg-gray-500/30"
                  >
                    <XCircle size={14} />
                    Close
                  </button>
                  <button
                    onClick={() => deleteContact(contact.id)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-500 rounded text-sm hover:bg-red-500/30 ml-auto"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            ))}

            {activeTab === "quotes" && quotes.length === 0 && (
              <div className="bg-[#121212] border border-[#27272A] rounded-lg p-8 text-center">
                <FileText className="mx-auto mb-4 text-gray-500" size={48} />
                <p className="text-gray-400">No quote requests yet</p>
              </div>
            )}

            {activeTab === "quotes" && quotes.map((quote) => (
              <div key={quote.id} className="bg-[#121212] border border-[#27272A] rounded-lg p-5">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#7C3AED]/20 rounded-full flex items-center justify-center">
                      <Wrench className="text-[#7C3AED]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{quote.name}</h3>
                      <p className="text-sm text-[#7C3AED]">{quote.service} • {quote.wheel_count} wheels</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                    {quote.status}
                  </span>
                </div>

                <p className="text-gray-300 mb-4">{quote.description}</p>

                {quote.wheel_size && (
                  <p className="text-sm text-gray-400 mb-4">
                    <strong>Wheel Size:</strong> {quote.wheel_size}
                  </p>
                )}

                <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-4">
                  <a href={`mailto:${quote.email}`} className="flex items-center gap-1 hover:text-[#7C3AED]">
                    <Mail size={14} />
                    {quote.email}
                  </a>
                  <a href={`tel:${quote.phone}`} className="flex items-center gap-1 hover:text-[#7C3AED]">
                    <Phone size={14} />
                    {quote.phone}
                  </a>
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {formatDate(quote.created_at)}
                  </span>
                </div>

                <div className="flex gap-2 pt-3 border-t border-[#27272A]">
                  <button
                    onClick={() => updateQuoteStatus(quote.id, "completed")}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-500/20 text-green-500 rounded text-sm hover:bg-green-500/30"
                  >
                    <CheckCircle size={14} />
                    Completed
                  </button>
                  <button
                    onClick={() => updateQuoteStatus(quote.id, "closed")}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-500/20 text-gray-400 rounded text-sm hover:bg-gray-500/30"
                  >
                    <XCircle size={14} />
                    Close
                  </button>
                  <button
                    onClick={() => deleteQuote(quote.id)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-500 rounded text-sm hover:bg-red-500/30 ml-auto"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
